var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useGlobalContext } from '@honeycomb/data';
import { Styler } from '@honeycomb/ui-core';
import { MapInfoWindow } from './MapInfoWindow';
import { MapMarkerCollection } from './MapMarkerCollection';
export function InteractiveMap(_a) {
    var { children, initialCenter, initialZoom = 15, zoomControl = false, mapTypeControl = false, scaleControl = false, streetViewControl = false, rotateControl = false, fullscreenControl = false, offscreen } = _a, other = __rest(_a, ["children", "initialCenter", "initialZoom", "zoomControl", "mapTypeControl", "scaleControl", "streetViewControl", "rotateControl", "fullscreenControl", "offscreen"]);
    const { configSettings: { GoogleMapsAPIKey }, } = useGlobalContext();
    // Refs
    const mapContainerRef = useRef(null);
    const markerCountRef = useRef(0);
    // State
    const [apiLoaded, setApiLoaded] = useState(false);
    const [map, setMap] = useState(null);
    const [selectedMarker, setSelectedMarker] = useState(null);
    const getGoogleMapsApi = React.useCallback(() => {
        return new Promise((resolve) => {
            if (!window.google) {
                // Load the API
                const onLoad = () => resolve(window.google);
                const script = document.createElement('script');
                script.src = `https://maps.googleapis.com/maps/api/js?key=${GoogleMapsAPIKey}&libraries=marker`;
                script.async = true;
                script.addEventListener(`load`, onLoad);
                if (document.body) {
                    document.body.appendChild(script);
                }
            }
            else {
                // API is already loaded
                resolve(window.google);
            }
        });
    }, [GoogleMapsAPIKey]);
    const mapControls = useMemo(() => {
        return {
            zoomControl,
            mapTypeControl,
            scaleControl,
            streetViewControl,
            rotateControl,
            fullscreenControl,
        };
    }, [fullscreenControl, mapTypeControl, rotateControl, scaleControl, streetViewControl, zoomControl]);
    useEffect(() => {
        getGoogleMapsApi()
            .then(() => {
            setApiLoaded(true);
        })
            // eslint-disable-next-line no-console
            .catch(console.error);
    }, [getGoogleMapsApi]);
    // Effect: Create the map when we are ready to do so.
    useEffect(() => {
        if (!apiLoaded || !window.google || map) {
            return;
        }
        // Create the map
        const mapOptions = Object.assign({ center: initialCenter, zoom: initialZoom, mapId: 'HONEYCOMB_MAP' }, mapControls);
        const gmap = new window.google.maps.Map(mapContainerRef.current, mapOptions);
        setMap(gmap);
    }, [apiLoaded, initialCenter, initialZoom, map, mapControls]);
    const handleBoundsKnown = (bounds) => {
        if (map !== null) {
            // Only use fitBounds when there's more than one marker or zoom level will be too high
            if (markerCountRef.current === 1) {
                map.setCenter(bounds.getCenter());
            }
            else {
                map.fitBounds(bounds);
            }
        }
    };
    let markers = null;
    let infoWindow = null;
    React.Children.forEach(children, (element) => {
        if (element == null) {
            return;
        }
        switch (element.type) {
            case MapMarkerCollection:
                markers = React.cloneElement(element, {
                    map: !offscreen ? map : null,
                    onBoundsKnown: (bounds) => handleBoundsKnown(bounds),
                    onMarkerSelected: (marker) => setSelectedMarker(marker),
                });
                markerCountRef.current = React.Children.count(element.props.children);
                break;
            case MapInfoWindow:
                infoWindow = React.cloneElement(element, {
                    map,
                    marker: selectedMarker,
                });
                break;
            default:
                break;
        }
    });
    return apiLoaded ? (React.createElement(React.Fragment, null,
        React.createElement(Styler, Object.assign({ ref: mapContainerRef, w: "100%", h: "100%" }, other),
            infoWindow != null && infoWindow,
            markers != null && markers),
        React.createElement("style", { 
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML: {
                __html: `
            .gm-style-iw.gm-style-iw-c {
                padding: 0;
            }

            .gm-style-iw-d {
                overflow: auto !important;
                max-height: none !important;
            }

            .gm-style-iw {
                max-height: none !important;
                overflow: auto !important;

                max-width: none !important;
            }

            body .gm-style .gm-style-iw-t::after {
                width: 16px;
                height: 16px;
                top: -1px;
            }

            button.gm-ui-hover-effect[aria-label="Close"] {
                display: none !important;
            }

            .gm-style-iw-tc::after,
            .gm-style-iw-tc::before {
                display: none !important;
            }

            .gm-style-iw-ch {
                display: contents !important;
            }
        `,
            } }))) : null;
}
